/**
 * Viewer identity. The client holds one bearer credential in localStorage and
 * sends it as `Authorization: jaivu <credential>` -- never in a URL, never a
 * cookie. The server issues the credential on your first real action (a vu)
 * and returns it once; simply visiting creates nothing.
 */
const KEY = 'jaivu.credential';
const OLD_KEY = 'jaivu.secret';   // pre-rename; migrated on first read

export const getCredential = (): string | null => {
  try {
    const v = localStorage.getItem(KEY);
    if (v) return v;
    // Identities issued before the rename live under the old key. Move them
    // across rather than silently logging everyone out.
    const old = localStorage.getItem(OLD_KEY);
    if (old) { localStorage.setItem(KEY, old); localStorage.removeItem(OLD_KEY); return old; }
    return null;
  } catch { return null; }
};
export const setCredential = (s: string) => {
  try { localStorage.setItem(KEY, s); } catch {}
};
export const clearCredential = () => {
  // Both keys. An identity issued before the rename can still be sitting under
  // the old one, and a "forget me" that left it there would hand the next
  // person at this machine the account it was supposed to take away.
  try { localStorage.removeItem(KEY); localStorage.removeItem(OLD_KEY); } catch {}
};

/** Authorization header when we have a credential, nothing when we do not. */
export function authHeaders(): Record<string, string> {
  const s = getCredential();
  return s ? { authorization: `jaivu ${s}` } : {};
}

export interface Me {
  id?: number; name?: string; iconId?: number | null;
  // Both artist-only, and absent rather than null for a visitor: the page
  // asks whether the field is there, not what it holds.
  isArtist?: true; artistPlace?: string | null;
}

/** Who the server says we are. {} for nobody. */
export async function me(): Promise<Me> {
  if (!getCredential()) return {};
  try { return await fetch('/api/me', { headers: authHeaders() }).then(r => r.json()); }
  catch { return {}; }
}

/** A write may issue an identity; store the credential the server hands back. */
export function adopt(r: { credential?: string }) {
  if (r && r.credential) setCredential(r.credential);
}
